import { useEffect, useMemo, useRef, useState } from "react";
import { format } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import type { Tables } from "@/integrations/supabase/types";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Image as ImageIcon, Video as VideoIcon, Music2, FileText, LayoutGrid } from "lucide-react";

type MediaCategory = "all" | "photos" | "videos" | "audio" | "documents";

type ObjectMediaRow = Tables<"object_media">;
type ObjectMediaAlbumRow = Tables<"object_media_album">;

interface MembersMediaProps {
  /**
   * When true, only show media where object_media.is_public = true.
   * When false (default), show all non-archived media.
   */
  publicOnly?: boolean;
  /** Controls whether documents are included in the gallery. Defaults to true. */
  includeDocuments?: boolean;
  /**
   * Controls whether the category tabs (All / Photos / Videos / Audio / Documents)
   * are visible. Defaults to true.
   */
  showCategoryTabs?: boolean;
  /**
   * Controls whether to show filename / extension / upload time under each thumbnail.
   * Defaults to true (shown). Set to false for the public Media page.
   */
  showItemMeta?: boolean;
  /**
   * When true, show the media name underneath video cards even if full meta is hidden.
   */
  showVideoTitles?: boolean;
}

function getCategoryForRow(row: ObjectMediaRow): MediaCategory {
  if (row.is_image) return "photos";
  if (row.is_video) return "videos";
  if (row.is_audio) return "audio";
  return "documents";
}

export default function MembersMedia({
  publicOnly = false,
  includeDocuments = true,
  showCategoryTabs = true,
  showItemMeta = true,
  showVideoTitles = false,
}: MembersMediaProps) {
  const [activeCategory, setActiveCategory] = useState<MediaCategory>("all");
  const [isLoadingMedia, setIsLoadingMedia] = useState(false);
  const [mediaItems, setMediaItems] = useState<ObjectMediaRow[]>([]);
  const [albums, setAlbums] = useState<ObjectMediaAlbumRow[]>([]);
  const [isLoadingAlbums, setIsLoadingAlbums] = useState(false);
  const [activeParentAlbumId, setActiveParentAlbumId] = useState<string | "all">("all");
  const [activeSubAlbumId, setActiveSubAlbumId] = useState<string | "all">("all");
  const [activeGradingYearFilter, setActiveGradingYearFilter] = useState<string | "all">("all");
  const [activeGradingQuarterFilter, setActiveGradingQuarterFilter] = useState<string | "all">("all");
  const [mediaTags, setMediaTags] = useState<
    Record<
      string,
      {
        id: string;
        album: ObjectMediaAlbumRow;
      }[]
    >
  >({});

  const [previewItem, setPreviewItem] = useState<ObjectMediaRow | null>(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  useEffect(() => {
    fetchMedia();
    fetchAlbums();

    // Keep in sync with changes from admin area
    const channel = supabase
      .channel("members_object_media_changes")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "object_media" },
        () => {
          fetchMedia();
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [publicOnly, includeDocuments]);

  const fetchMedia = async () => {
    try {
      setIsLoadingMedia(true);
      let query = supabase
        .from("object_media")
        .select("*")
        .eq("archived", false);

      if (publicOnly) {
        query = query.eq("is_public", true);
      }

      if (!includeDocuments) {
        query = query.or("is_image.eq.true,is_video.eq.true,is_audio.eq.true");
      }

      const { data, error } = await query.order("create_time", {
        ascending: false,
      });

      if (error) throw error;
      setMediaItems((data as ObjectMediaRow[]) ?? []);
    } catch (error) {
      console.error("Error loading media for members:", error);
      setMediaItems([]);
    } finally {
      setIsLoadingMedia(false);
    }
  };

  const fetchAlbums = async () => {
    try {
      setIsLoadingAlbums(true);
      const { data, error } = await supabase
        .from("object_media_album")
        .select("*")
        .eq("archived", false)
        .order("album_name", { ascending: true });

      if (error) throw error;
      const albumsData = (data as ObjectMediaAlbumRow[]) ?? [];
      setAlbums(albumsData);
      await fetchMediaTags(albumsData);
    } catch (error) {
      console.error("Error loading albums for members:", error);
      setAlbums([]);
      setMediaTags({});
    } finally {
      setIsLoadingAlbums(false);
    }
  };

  const fetchMediaTags = async (albumsList: ObjectMediaAlbumRow[]) => {
    try {
      if (!albumsList || albumsList.length === 0) {
        setMediaTags({});
        return;
      }

      const { data, error } = await supabase
        .from("object_media_tagged")
        .select("*")
        .eq("archived", false);

      if (error) {
        console.error("Error loading media tags for members:", error);
        setMediaTags({});
        return;
      }

      const albumMap = new Map<string, ObjectMediaAlbumRow>();
      albumsList.forEach((album) => {
        albumMap.set(album.id, album);
      });

      const tagsByMedia: Record<
        string,
        {
          id: string;
          album: ObjectMediaAlbumRow;
        }[]
      > = {};

      (data as any[]).forEach((tag) => {
        const album = albumMap.get(tag.oma_id);
        if (!album) return;
        if (!tagsByMedia[tag.om_id]) {
          tagsByMedia[tag.om_id] = [];
        }
        tagsByMedia[tag.om_id].push({
          id: tag.id,
          album,
        });
      });

      setMediaTags(tagsByMedia);
    } catch (error) {
      console.error("Unexpected error loading media tags for members:", error);
      setMediaTags({});
    }
  };

  const groupedMediaByDate = useMemo(() => {
    const groups: Record<string, ObjectMediaRow[]> = {};

    mediaItems.forEach((item) => {
      const createdAt = item.create_time;
      if (!createdAt) return;

      const dateKey = format(new Date(createdAt), "yyyy-MM-dd");
      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }
      groups[dateKey].push(item);
    });

    return groups;
  }, [mediaItems]);

  const visibleAlbumUsage = useMemo(() => {
    const usedAlbumIds = new Set<string>();
    if (!mediaItems.length || !Object.keys(mediaTags).length) {
      return usedAlbumIds;
    }

    const visibleIds = new Set(mediaItems.map((m) => m.id));

    Object.entries(mediaTags).forEach(([mediaId, tags]) => {
      if (!visibleIds.has(mediaId)) return;
      tags.forEach((tag) => {
        usedAlbumIds.add(tag.album.id);
      });
    });

    return usedAlbumIds;
  }, [mediaItems, mediaTags]);

  const mediaMatchesAlbumFilter = (item: ObjectMediaRow) => {
    if (activeParentAlbumId === "all") {
      return true;
    }

    const tagsForMedia = mediaTags[item.id] ?? [];
    if (tagsForMedia.length === 0) {
      return false;
    }

    const parentAlbum = albums.find((a) => a.id === activeParentAlbumId);
    const parentName = parentAlbum?.album_name.toLowerCase() ?? "";
    const isStandardGradingParent =
      parentName === "grading" || parentName === "taekwondo grading";

    if (isStandardGradingParent) {
      // 1) If a specific group (sub-folder) is selected, require that tag
      if (activeSubAlbumId !== "all") {
        return tagsForMedia.some((tag) => tag.album.id === activeSubAlbumId);
      }

      // 2) If a specific grading year AND quarter are selected, match any grading tag for that combo
      if (activeGradingYearFilter !== "all" && activeGradingQuarterFilter !== "all") {
        return tagsForMedia.some((tag) => {
          const name = tag.album.album_name;
          const parts = name.split("-");
          if (parts.length < 3) return false;
          const [year, , quarter] = parts;
          return year === activeGradingYearFilter && quarter === activeGradingQuarterFilter;
        });
      }

      // 3) If only a specific grading year is selected, match any grading tag for that year
      if (activeGradingYearFilter !== "all") {
        return tagsForMedia.some((tag) => {
          const name = tag.album.album_name;
          const [year] = name.split("-");
          return year === activeGradingYearFilter;
        });
      }

      // 4) Grading parent, ALL years & ALL quarters -> any grading tag
      return tagsForMedia.some((tag) => tag.album.parent_id === activeParentAlbumId);
    }

    if (activeSubAlbumId !== "all") {
      return tagsForMedia.some((tag) => tag.album.id === activeSubAlbumId);
    }

    return tagsForMedia.some((tag) => tag.album.id === activeParentAlbumId);
  };

  const filteredMediaByCategory = useMemo(() => {
    const result: Record<string, ObjectMediaRow[]> = {};

    Object.entries(groupedMediaByDate).forEach(([date, items]) => {
      const filtered = items.filter((item) => {
        const matchesCategory =
          activeCategory === "all" || getCategoryForRow(item) === activeCategory;
        if (!matchesCategory) return false;
        return mediaMatchesAlbumFilter(item);
      });
      if (filtered.length > 0) {
        result[date] = filtered;
      }
    });

    return result;
  }, [
    groupedMediaByDate,
    activeCategory,
    activeParentAlbumId,
    activeSubAlbumId,
    activeGradingYearFilter,
    mediaTags,
    albums,
  ]);

  const [visibleCount, setVisibleCount] = useState(40);

  useEffect(() => {
    setVisibleCount(40);
  }, [
    activeCategory,
    activeParentAlbumId,
    activeSubAlbumId,
    activeGradingYearFilter,
    activeGradingQuarterFilter,
  ]);

  const {
    paginatedByDate,
    totalItemCount,
    hasMore,
  } = useMemo(() => {
    const entries = Object.entries(filteredMediaByCategory).sort(([a], [b]) =>
      a < b ? 1 : -1,
    );

    let remaining = visibleCount;
    const paginated: Record<string, ObjectMediaRow[]> = {};
    let total = 0;

    for (const [date, items] of entries) {
      total += items.length;
      if (remaining <= 0) continue;

      if (items.length <= remaining) {
        paginated[date] = items;
        remaining -= items.length;
      } else {
        paginated[date] = items.slice(0, remaining);
        remaining = 0;
      }
    }

    return {
      paginatedByDate: paginated,
      totalItemCount: total,
      hasMore: total > visibleCount,
    };
  }, [filteredMediaByCategory, visibleCount]);

  const loadMoreRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!hasMore) return;
    const el = loadMoreRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting && hasMore && !isLoadingMedia) {
          setVisibleCount((prev) => prev + 40);
        }
      },
      { rootMargin: "200px" },
    );

    observer.observe(el);

    return () => {
      observer.disconnect();
    };
  }, [hasMore, isLoadingMedia]);

  const getDerivedUrl = (
    item: ObjectMediaRow,
    variant: "thumb300" | "med1200",
  ) => {
    if (variant === "thumb300" && (item as any).thumb_url) {
      return (item as any).thumb_url as string;
    }
    if (variant === "med1200" && (item as any).medium_url) {
      return (item as any).medium_url as string;
    }

    const baseUrl = ((item as any).original_url as string | null) || item.url;

    try {
      const url = new URL(baseUrl);
      const parts = url.pathname.split("/").filter(Boolean);
      if (parts.length === 0) return baseUrl;

      const filename = parts.pop()!;
      const dot = filename.lastIndexOf(".");
      const baseName = dot >= 0 ? filename.slice(0, dot) : filename;

      // New-style layout: /media/original/<id>.<ext> -> /media/thumb300|med1200/<id>.webp
      const mediaIndex = parts.indexOf("media");
      const originalIndex = parts.indexOf("original");
      if (
        mediaIndex !== -1 &&
        originalIndex !== -1 &&
        originalIndex === mediaIndex + 1
      ) {
        const newParts = [
          "",
          "media",
          variant === "thumb300" ? "thumb300" : "med1200",
          `${baseName}.webp`,
        ];
        url.pathname = newParts.join("/").replace(/\/+/g, "/");
        return url.toString();
      }

      // Legacy layout: <dir>/<filename>.<ext> -> <dir>/<thumb300|med1200>/<filename>.webp
      const lastDir = parts.pop() || "";
      const newPath = [
        "",
        ...parts,
        lastDir,
        variant === "thumb300" ? "thumb300" : "med1200",
        `${baseName}.webp`,
      ]
        .filter(Boolean)
        .join("/");

      url.pathname = newPath.replace(/\/+/g, "/");
      return url.toString();
    } catch {
      return baseUrl;
    }
  };

  const renderMediaPreview = (item: ObjectMediaRow) => {
    const category = getCategoryForRow(item);

    if (category === "photos") {
      const thumbUrl = getDerivedUrl(item, "thumb300");
      return (
        <div className="h-28 w-full rounded-md bg-slate-100 flex items-center justify-center overflow-hidden">
          <img
            src={thumbUrl}
            alt={item.name ?? "Photo"}
            className="max-h-full max-w-full object-contain"
            loading="lazy"
            decoding="async"
            onError={(e) => {
              if (e.currentTarget.src !== item.url) {
                e.currentTarget.src = item.url;
              }
            }}
          />
        </div>
      );
    }

    if (category === "videos") {
      return (
        <div className="h-32 w-full rounded-md bg-black overflow-hidden">
          <video
            src={item.url}
            className="h-full w-full object-cover"
            muted
            playsInline
            preload="metadata"
          />
        </div>
      );
    }

    if (category === "audio") {
      return (
        <div className="flex h-32 w-full items-center justify-center rounded-md bg-indigo-50 text-indigo-700">
          <Music2 className="h-8 w-8 mr-2" />
          <span className="text-sm">Audio</span>
        </div>
      );
    }

    return (
      <div className="flex h-32 w-full items-center justify-center rounded-md bg-slate-50 text-slate-700">
        <FileText className="h-8 w-8 mr-2" />
        <span className="text-sm">Document</span>
      </div>
    );
  };

  const renderPreviewDialogContent = (item: ObjectMediaRow) => {
    const category = getCategoryForRow(item);

    if (category === "photos") {
      const mediumUrl = getDerivedUrl(item, "med1200");
      return (
        <div className="space-y-3">
          <img
            src={mediumUrl}
            alt={item.name ?? "Photo preview"}
            className="w-full max-h-[70vh] object-contain rounded-md bg-slate-100"
            loading="lazy"
            decoding="async"
            onError={(e) => {
              if (e.currentTarget.src !== item.url) {
                e.currentTarget.src = item.url;
              }
            }}
          />
        </div>
      );
    }

    if (category === "videos") {
      return (
        <div className="space-y-3">
          <video
            src={item.url}
            controls
            className="w-full max-h-[70vh] rounded-md bg-black"
          />
        </div>
      );
    }

    if (category === "audio") {
      return (
        <div className="space-y-3">
          <audio src={item.url} controls className="w-full" />
        </div>
      );
    }

    return (
      <div className="space-y-3">
        <div className="h-72 w-full rounded-md border bg-slate-50 overflow-hidden">
          <iframe
            src={item.url}
            className="h-full w-full"
            title={item.name ?? "Document preview"}
          />
        </div>
        <p className="text-xs text-muted-foreground">
          If the document does not render correctly, you can open it in a new tab.
        </p>
        <Button asChild variant="outline" size="sm">
          <a href={item.url} target="_blank" rel="noreferrer">
            Open in new tab
          </a>
        </Button>
      </div>
    );
  };

  const getItemDisplayName = (item: ObjectMediaRow) => item.name ?? item.url.split("/").pop();

  // Only show non-empty albums and sub-folders
  const nonEmptyParentAlbums = useMemo(() => {
    if (!albums.length) return [];
    const parents = albums.filter((a) => !a.parent_id);
    if (!visibleAlbumUsage.size) return [];

    return parents.filter((parent) => {
      if (visibleAlbumUsage.has(parent.id)) return true;
      const hasChildWithMedia = albums.some(
        (child) => child.parent_id === parent.id && visibleAlbumUsage.has(child.id),
      );
      return hasChildWithMedia;
    });
  }, [albums, visibleAlbumUsage]);

  return (
    <div className="space-y-4">
      <Tabs
        value={activeCategory}
        onValueChange={(value) => setActiveCategory(value as MediaCategory)}
        className="w-full"
      >
        {showCategoryTabs && (
          <TabsList className="mb-4 w-full">
            <TabsTrigger value="all" className="flex-1 flex items-center justify-center gap-1 sm:gap-2">
              <LayoutGrid className="h-4 w-4" />
              <span className="hidden sm:inline">All</span>
            </TabsTrigger>
            <TabsTrigger value="photos" className="flex-1 flex items-center justify-center gap-1 sm:gap-2">
              <ImageIcon className="h-4 w-4" />
              <span className="hidden sm:inline">Photos</span>
            </TabsTrigger>
            <TabsTrigger value="videos" className="flex-1 flex items-center justify-center gap-1 sm:gap-2">
              <VideoIcon className="h-4 w-4" />
              <span className="hidden sm:inline">Videos</span>
            </TabsTrigger>
            <TabsTrigger value="audio" className="flex-1 flex items-center justify-center gap-1 sm:gap-2">
              <Music2 className="h-4 w-4" />
              <span className="hidden sm:inline">Audio</span>
            </TabsTrigger>
            {includeDocuments && <TabsTrigger value="documents" className="flex-1 flex items-center justify-center gap-1 sm:gap-2">
              <FileText className="h-4 w-4" />
              <span className="hidden sm:inline">Documents</span>
            </TabsTrigger>}
          </TabsList>
        )}

        {/* Album Filter Bar - parents only, hide empty albums */}
        <div className="mb-2 flex flex-wrap items-center gap-2">
          <span className="text-xs font-semibold text-muted-foreground mr-1">
            Albums:
          </span>
          <Button
            variant={activeParentAlbumId === "all" ? "default" : "outline"}
            size="sm"
            onClick={() => {
              setActiveParentAlbumId("all");
              setActiveSubAlbumId("all");
              setActiveGradingYearFilter("all");
              setActiveGradingQuarterFilter("all");
            }}
          >
            ALL
          </Button>
          {isLoadingAlbums && (
            <span className="text-xs text-muted-foreground">Loading albums...</span>
          )}
          {!isLoadingAlbums &&
            nonEmptyParentAlbums.map((album) => (
              <Button
                key={album.id}
                variant={activeParentAlbumId === album.id ? "default" : "outline"}
                size="sm"
                onClick={() => {
                  setActiveParentAlbumId(album.id);
                  setActiveSubAlbumId("all");
                  setActiveGradingYearFilter("all");
                  setActiveGradingQuarterFilter("all");
                }}
              >
                {album.album_name}
              </Button>
            ))}
        </div>

        {/* Folder Filter Bar (for selected parent), hide empty folders */}
        {activeParentAlbumId !== "all" && (
          (() => {
            const parentAlbum = albums.find(
              (album) => album.id === activeParentAlbumId,
            );
            if (!parentAlbum) return null;

            const parentName = parentAlbum.album_name.toLowerCase();
            const isStandardGradingParent =
              parentName === "grading" || parentName === "taekwondo grading";

            const children = albums.filter(
              (album) =>
                album.parent_id === activeParentAlbumId &&
                visibleAlbumUsage.has(album.id),
            );

            if (!isStandardGradingParent) {
              if (children.length === 0) return null;
              return (
                <div className="mb-4 flex flex-wrap items-center gap-2">
                  <span className="text-xs font-semibold text-muted-foreground mr-1">
                    Folders:
                  </span>
                  <Button
                    variant={activeSubAlbumId === "all" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setActiveSubAlbumId("all")}
                  >
                    ALL
                  </Button>
                  {children.map((album) => (
                    <Button
                      key={album.id}
                      variant={activeSubAlbumId === album.id ? "default" : "outline"}
                      size="sm"
                      onClick={() => setActiveSubAlbumId(album.id)}
                    >
                      {album.album_name}
                    </Button>
                  ))}
                </div>
              );
            }

            // Special taxonomy for Taekwondo Grading: YEAR -> QUARTER -> GROUP
            const gradingChildren = albums.filter(
              (album) =>
                album.parent_id === activeParentAlbumId &&
                visibleAlbumUsage.has(album.id),
            );

            if (gradingChildren.length === 0) return null;

            type GroupEntry = { id: string; quarter: string; group: string };

            const byYear = gradingChildren.reduce<
              Record<string, Record<string, GroupEntry[]>>
            >((acc, album) => {
              const parts = album.album_name.split("-");
              if (parts.length < 4) return acc;
              const [year, , quarter, group] = parts;
              if (!year || !quarter || !group) return acc;
              if (!acc[year]) acc[year] = {};
              if (!acc[year][quarter]) acc[year][quarter] = [];
              acc[year][quarter].push({ id: album.id, quarter, group });
              return acc;
            }, {});

            const years = Object.keys(byYear).sort((a, b) => (a < b ? 1 : -1));
            const quarterOrder = ["Q1", "Q2", "Q3", "Q4"];

            const quartersForYear =
              activeGradingYearFilter !== "all" && byYear[activeGradingYearFilter]
                ? Object.keys(byYear[activeGradingYearFilter]).sort(
                    (a, b) => quarterOrder.indexOf(a) - quarterOrder.indexOf(b),
                  )
                : [];

            const groupsForSelection =
              activeGradingYearFilter !== "all" &&
              activeGradingQuarterFilter !== "all" &&
              byYear[activeGradingYearFilter] &&
              byYear[activeGradingYearFilter][activeGradingQuarterFilter]
                ? byYear[activeGradingYearFilter][activeGradingQuarterFilter]
                : [];

            return (
              <>
                {/* Years row */}
                <div className="mb-2 flex flex-wrap items-center gap-2">
                  <span className="text-xs font-semibold text-muted-foreground mr-1">
                    Years:
                  </span>
                  <Button
                    variant={
                      activeGradingYearFilter === "all" ? "default" : "outline"
                    }
                    size="sm"
                    onClick={() => {
                      setActiveGradingYearFilter("all");
                      setActiveGradingQuarterFilter("all");
                      setActiveSubAlbumId("all");
                    }}
                  >
                    ALL
                  </Button>
                  {years.map((year) => (
                    <Button
                      key={year}
                      variant={
                        activeGradingYearFilter === year ? "default" : "outline"
                      }
                      size="sm"
                      onClick={() => {
                        setActiveGradingYearFilter(year);
                        setActiveGradingQuarterFilter("all");
                        setActiveSubAlbumId("all");
                      }}
                    >
                      {year}
                    </Button>
                  ))}
                </div>

                {/* Quarters row for selected year */}
                {activeGradingYearFilter !== "all" && quartersForYear.length > 0 && (
                  <div className="mb-2 flex flex-wrap items-center gap-2">
                    <span className="text-xs font-semibold text-muted-foreground mr-1">
                      Quarters:
                    </span>
                    <Button
                      variant={
                        activeGradingQuarterFilter === "all" ? "default" : "outline"
                      }
                      size="sm"
                      onClick={() => {
                        setActiveGradingQuarterFilter("all");
                        setActiveSubAlbumId("all");
                      }}
                    >
                      ALL
                    </Button>
                    {quartersForYear.map((q) => (
                      <Button
                        key={q}
                        variant={
                          activeGradingQuarterFilter === q ? "default" : "outline"
                        }
                        size="sm"
                        onClick={() => {
                          setActiveGradingQuarterFilter(q);
                          setActiveSubAlbumId("all");
                        }}
                      >
                        {q}
                      </Button>
                    ))}
                  </div>
                )}

                {/* Groups row for selected year + quarter */}
                {activeGradingYearFilter !== "all" &&
                  activeGradingQuarterFilter !== "all" &&
                  groupsForSelection.length > 0 && (
                    <div className="mb-4 flex flex-wrap items-center gap-2">
                      <span className="text-xs font-semibold text-muted-foreground mr-1">
                        Groups:
                      </span>
                      <Button
                        variant={activeSubAlbumId === "all" ? "default" : "outline"}
                        size="sm"
                        onClick={() => setActiveSubAlbumId("all")}
                      >
                        ALL
                      </Button>
                      {groupsForSelection.map((entry) => (
                        <Button
                          key={entry.id}
                          variant={
                            activeSubAlbumId === entry.id ? "default" : "outline"
                          }
                          size="sm"
                          onClick={() => setActiveSubAlbumId(entry.id)}
                        >
                          {entry.group}
                        </Button>
                      ))}
                    </div>
                  )}
              </>
            );
          })()
        )}

        <TabsContent value={activeCategory}>
          {isLoadingMedia ? (
            <p className="text-sm text-muted-foreground">Loading media...</p>
          ) : Object.keys(paginatedByDate).length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No media available in this category yet.
            </p>
          ) : (
            <ScrollArea className="h-[480px] pr-4">
              <div className="space-y-6">
                {Object.entries(paginatedByDate)
                  .sort(([a], [b]) => (a < b ? 1 : -1))
                  .map(([dateKey, items]) => (
                    <div key={dateKey} className="space-y-2">
                      <div className="flex items-center gap-2">
                        <h3 className="text-sm font-semibold">
                          {format(new Date(dateKey), "EEEE, dd MMM yyyy")}
                        </h3>
                        <Badge variant="outline">{items.length}</Badge>
                      </div>
                      <div className="grid gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
                        {items.map((item) => (
                          <Card
                            key={item.id}
                            className="overflow-hidden cursor-pointer transition-all border hover:border-slate-300"
                            onClick={() => {
                              setPreviewItem(item);
                              setIsPreviewOpen(true);
                            }}
                          >
                            {renderMediaPreview(item)}
                            {showItemMeta && (
                              <div className="space-y-1 p-3">
                                <p className="line-clamp-1 text-xs font-medium">
                                  {getItemDisplayName(item)}
                                </p>
                                <p className="text-[10px] text-muted-foreground">
                                  {item.extension.toUpperCase()} •{" "}
                                  {format(new Date(item.create_time), "HH:mm")}
                                </p>
                              </div>
                            )}
                            {!showItemMeta &&
                              showVideoTitles &&
                              getCategoryForRow(item) === "videos" && (
                                <div className="p-3 pt-2">
                                  <p className="line-clamp-2 text-xs font-medium">
                                    {getItemDisplayName(item)}
                                  </p>
                                </div>
                              )}
                          </Card>
                        ))}
                      </div>
                    </div>
                  ))}
                {hasMore && <div ref={loadMoreRef} className="h-px w-full" />}
              </div>
            </ScrollArea>
          )}
        </TabsContent>
      </Tabs>

      <Dialog
        open={isPreviewOpen && !!previewItem}
        onOpenChange={(open) => {
          setIsPreviewOpen(open);
          if (!open) {
            setPreviewItem(null);
          }
        }}
      >
        <DialogContent className="max-w-3xl">
          {previewItem && renderPreviewDialogContent(previewItem)}
        </DialogContent>
      </Dialog>
    </div>
  );
}

